import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import useAvailibleMenu from "./useAvailibleMenu";
import MenuType from "./menuTypeConst";
import { FeatureKey } from "@/types/generated/FeatureKey";
import { hasFeature } from "@/hooks/useFeatures";

const usePartnerMenu = (loginInfo) => {
  const language = useSelector((state) => state.language);

  const routes = useMemo(() => {
    const { features, permissionsMap, lastOrganisationFilters } = loginInfo;

    return [
      {
        collapse: true,
        name: language.trackers_and_sensors_key,
        state: "openTrackersSensors",
        icon: (
          <span className="tracker-profile-admin-menu-icon menu-icon"></span>
        ),
        views: [
          {
            view: permissionsMap["MENU_ADMIN_TRACKERS"],
            to: "/trackers_partner",
            layout: "",
            name: language.UserPermission_MENU_ADMIN_TRACKERS_description_key,
            subPages: [
              "/trackers_admin_update",
              "/import_tracker_settings_partner",
            ],
          },
          {
            view: permissionsMap["MENU_ADMIN_USAGE_PROFILE"],
            to: "/device_usage_profile_partner",
            layout: "",
            name:
              language.UserPermission_MENU_ADMIN_USAGE_PROFILE_description_key,
            subPages: ["/device_usage_profile_manage_partner"],
          },
          {
            view: permissionsMap["MENU_ADMIN_BLE_SENSOR"],
            to: "/ble_sensor_partner",
            layout: "",
            name: language.UserPermission_MENU_ADMIN_BLE_SENSOR_description_key,
            subPages: ["/ble_sensor_update", "/ble_sensor_import"],
          },
          {
            view:
              features["bulk_operations"] &&
              permissionsMap["MENU_ADMIN_BULK_OPERATION"],
            to: "/bulk_manage_partner",
            layout: "",
            name: language.sidebar_admin_bulk_operation_title_key,
            subPages: [
              "/bulk_operations_partner",
              "/bulk_results_detail_partner",
            ],
          },
        ],
      },
      {
        collapse: true,
        name: language.location_key,
        state: "openLocation",
        icon: <span className="location-stop-icon menu-icon"></span>,
        views: [
          {
            view:
              features["geozone_manager"] &&
              permissionsMap["MENU_ADMIN_GEOZONES"],
            to: "/areas_partner",
            layout: "",
            name: language.UserPermission_MENU_ADMIN_GEOZONES_description_key,
            subPages: ["/areas_update"],
          },
          {
            view:
              features["loc_beacon"] &&
              permissionsMap["MENU_ADMIN_INFRASTRUCTURE"],
            to: "/infrastructures_partner",
            layout: "",
            name:
              language.UserPermission_MENU_ADMIN_INFRASTRUCTURE_description_key,
            subPages: ["/infrastructures_update", "/import_infrastructure_settings_partner"],
          },
        ],
      },
      {
        collapse: true,
        name: language.organizations_key,
        state: "openOrgSetting",
        icon: <span className="org-admin-menu-icon menu-icon"></span>,
        views: [
          {
            view:
              permissionsMap["MENU_ADMIN_ORGANIZATION_LIST"] &&
              features["partner_org_manage"] &&
              !lastOrganisationFilters,
            to: `/organization`,
            layout: "",
            name:
              language.UserPermission_MENU_ADMIN_ORGANIZATION_LIST_description_key,
          },
          {
            view:
              features["demo_environment"] && permissionsMap["MENU_ADMIN_DEMO"],
            to: "/org_demo",
            layout: "",
            name: language.UserPermission_MENU_ADMIN_DEMO_description_key,
            subPages: ["/org_demo_manage"],
          },
          {
            view: permissionsMap["MENU_ADMIN_QUOTES"],
            to: "/quotes",
            layout: "",
            name: language.quotes_key,
            subPages: ["/quote_detail"],
          },
        ],
      },
      {
        collapse: !lastOrganisationFilters,
        name: language.access_management_key,
        state: "openParnerOrg",
        icon: <span className="access-icon menu-icon"></span>,
        views: [
          {
            view:
              features["user_profile"] &&
              permissionsMap["MENU_ADMIN_USER_ROLE"],
            to: "/role_manager",
            layout: "",
            name: language.UserPermission_MENU_ADMIN_USER_ROLE_description_key,
            subPages: ["/role_manager_update"],
          },
          {
            view:
              features["organisation_visibility_filters"] &&
              permissionsMap["MENU_ADMIN_ORGANISATION_VISIBILITY_FILTER"],
            to: "/manage_organisation_filters_partner",
            layout: "",
            name:
              language.UserPermission_MENU_ADMIN_ORGANISATION_VISIBILITY_FILTER_description_key,
            subPages: ["/manage_partner_organisation_filters_update"],
          },
        ],
      },
      {
        collapse: !lastOrganisationFilters,
        name: language.legal,
        state: "openLegal",
        icon: <span className="legal-icon menu-icon"></span>,
        views: [
          {
            view:
              permissionsMap["MENU_ADMIN_LEGAL_DOCUMENTS"] &&
              features["legal_document"],
            to: "/legal_document",
            layout: "",
            name:
              language.UserPermission_MENU_ADMIN_LEGAL_DOCUMENTS_description_key,
            subPages: [
              "/legal_document_import",
              "/legal_document_send",
              "/legal_document_view",
            ],
          },
        ],
      },
      {
        collapse: !lastOrganisationFilters,
        name: language.customization_key,
        state: "opencustomization",
        icon: <span className="ui-custom-icon menu-icon"></span>,
        views: [
          {
            view:
              features["landing_page"] &&
              features["partner_org_manage"] &&
              permissionsMap["MENU_ADMIN_LANDING_PAGE"],
            to: `/default_landing_page_partner`,
            layout: "",
            name: language.default_landing_page_key,
          },
          {
            view:
              features["report_generator"] &&
              permissionsMap["MENU_ADMIN_REPORT_TEMPLATE"],
            to: "/report_template_library_partner",
            layout: "",
            name:
              language.UserPermission_MENU_ADMIN_REPORT_TEMPLATE_description_key,
            subPages: ["/report_template_library_manage_partner"],
          },
          {
            view:
              features["custom_interface"] &&
              features["partner_org_manage"] &&
              permissionsMap["MENU_ADMIN_CUSTOM_INTERFACE"],
            to: `/custom_branding_partner`,
            layout: "",
            name: language.organisation_custom_branding_key,
          },
          {
            view: permissionsMap["MENU_ADMIN_SUPPORT"],
            to: `/support_partner`,
            layout: "",
            name: language.UserPermission_MENU_ADMIN_SUPPORT_description_key,
          },
          {
            view:
              features["dashboard"] && permissionsMap["MENU_ADMIN_DASHBOARD"],
            to: "/bi_dashboards_partner",
            layout: "",
            name: language.UserPermission_MENU_ADMIN_DASHBOARD_description_key,
            subPages: ["/bi_dashboard_manage_partner"],
          },
          {
            view:
              hasFeature(FeatureKey.audit_log) &&
              permissionsMap["MENU_ADMIN_AUDIT_LOG"],
            to: `/audit_log_partner`,
            layout: "",
            name: language.UserPermission_MENU_ADMIN_AUDIT_LOG_description_key,
            subPages: ["/audit_log_view_partner"],
          },
        ],
      },
      {
        collapse: true,
        name: language.customization_key,
        state: "opencustomization",
        icon: <span className="ui-custom-icon menu-icon"></span>,
        views: [
          {
            view:
              features["image_library"] &&
              permissionsMap["MENU_ADMIN_IMAGE_LIBRARY"],
            to: "/image_library_all",
            layout: "",
            name:
              language.UserPermission_MENU_ADMIN_IMAGE_LIBRARY_description_key,
            subPages: ["/image_library_manage"],
          },
        ],
      },
      {
        collapse: true,
        name: language.sidebar_support_key,
        state: "openInsights",
        icon: <span className="support-icon menu-icon"></span>,
        views: [
          {
            view:
              permissionsMap["MENU_ADMIN_RMA_DECLARATION"] &&
              !lastOrganisationFilters,
            to: `/rma_declaration`,
            layout: "",
            name:
              language.UserPermission_MENU_ADMIN_RMA_DECLARATION_description_key,
            subPages: ["/rma_declaration_manage"],
          },
        ],
      },
      {
        collapse: true,
        name: language.platform_notifications_key,
        state: "openLocation",
        icon: <span className="notification-icon menu-icon"></span>,
        views: [
          {
            view:
              permissionsMap["MENU_ADMIN_NOTIFICATION"] &&
              !lastOrganisationFilters,
            to: "/notification",
            layout: "",
            name:
              language.UserPermission_MENU_ADMIN_NOTIFICATION_description_key,
            subPages: ["/notification_update"],
          },
        ],
      },
    ];
  }, [loginInfo, language]);

  const [data, setData] = useState({
    type: MenuType.ALL,
    loading: true,
    routes: [],
    availableMenuPaths: {},
    allPaths: {},
    defaultPath: "/organization",
  });

  const availibleMenuData = useAvailibleMenu(routes, "/organization");

  useEffect(() => {
    setData({ ...data, ...availibleMenuData, loading: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availibleMenuData]);

  return data;
};

export default usePartnerMenu;
